import BossModel from "@/models/BossModel";
import Detail from "@/models/Product/Detail/Detail";
import Region from "@/models/Region/Region";
import Notice from "@/models/Product/Notice/Notice";
import ProductVersion from "@/models/Product/ProductVersion/ProductVersion";
import ProductInternalTag from "@/models/Product/ProductInternalTag/ProductInternalTag";
import AvailableDate from '@/models/Product/AvailableDate/AvailableDate'
import AvailableDayOfWeek from '@/models/Product/AvailableDay/AvailableDay'
import constants from '@/constants';
import WebsiteProduct from "@/models/WebsiteProduct/WebsiteProduct";
import ProductWebsiteProductTag from "@/models/Product/WebsiteProductTag/WebsiteProductTag";
import RelatedProduct from "@/models/RelatedProduct/RelatedProduct";
import Daytoday from "@/models/Product/Daytoday/Daytoday";
import ProductUniqueSellingPoint from "@/models/Product/UniqueSellingPoint/UniqueSellingPoint";
import WebsiteRoute from "@/models/WebsiteRoute/WebsiteRoute";
import WebsiteIncludeTransfer from "@/models/WebsiteIncludeTransfer/WebsiteIncludeTransfer";
import ProductAddress from "@/models/Product/ProductAddress/ProductAddress";
import Package from "@/models/Package/Package";
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import mapKeys from "lodash/mapKeys";
import groupBy from "lodash/groupBy";
import ProductInternalTagProduct from "@/models/Product/ProductInternalTagProduct";
import ProductRegion from "@/models/Product/ProductRegion";
import Line from "@/models/Package/Line/Line";
import Element from "@/models/Product/PriceExtension/Element/Element";
import Group from "@/models/Product/Discount/Group/Group.js";
import WebsiteHighlight from "@/models/WebsiteHighlight/WebsiteHighlight.js";

export default class Product extends BossModel {

    /**
     *
     * @type {string}
     */
    static apiUrl = 'product';

    /**
     *
     * @type {string}
     */
    static entity = 'products';

    static fields() {
        return {
            id: this.number(null).nullable(),
            active_version_id: this.number(null).nullable(),
            queued_for_today_at: this.attr(null),
            calculated_for_today_at: this.attr(null),
            travalco_eid: this.attr(null),
            travalco_code: this.attr(null),
            rate_hawk_eid: this.attr(null),
            website_highlight_id: this.number(null).nullable(),
            travalcoFacilities: this.attr([]),
            travalcoDetails: this.attr([]),
            has_unsaved_concepts: this.boolean(false),
            details: this.hasMany(Detail, 'product_id'),
            activeDetails: this.hasMany(Detail, 'product_id'),
            webDetails: this.hasMany(Detail, 'product_id'),
            notices: this.hasMany(Notice, 'product_id'),
            addresses: this.hasMany(ProductAddress, 'product_id'),
            productUsps: this.hasMany(ProductUniqueSellingPoint, 'product_id'),
            relatedProducts: this.hasMany(RelatedProduct, 'product_id'),
            relatedProductsHotels: this.hasMany(RelatedProduct, 'product_id'),
            relatedProductsRelated: this.hasMany(RelatedProduct, 'product_id'),
            relatedProductsUpgrades: this.hasMany(RelatedProduct, 'product_id'),
            relatedProductsDowngrades: this.hasMany(RelatedProduct, 'product_id'),
            websiteProduct: this.hasOne(WebsiteProduct, 'product_id'),
            productInternalTags: this.belongsToMany(
                ProductInternalTag,
                ProductInternalTagProduct,
                'product_id',
                'product_internal_tag_id'
            ),
            websiteProductTags: this.hasMany(ProductWebsiteProductTag, 'product_id'),
            regions: this.belongsToMany(
                Region,
                ProductRegion,
                'product_id',
                'region_id'
            ),
            daytodays: this.hasMany(Daytoday, 'product_id'),
            packages: this.belongsToMany(Package, Line, 'product_id', 'package_id'),
            lines: this.hasMany(Line, 'product_id'),
            websiteIncludeTransfers: this.hasMany(WebsiteIncludeTransfer, 'product_id'),
            discountGroups: this.hasMany(Group, 'product_id'),
            websiteRoute: this.hasOne(WebsiteRoute, 'routeable_id'),
            activeVersion: this.belongsTo(ProductVersion, 'active_version_id'),
            availableDays: this.hasMany(AvailableDayOfWeek, 'product_id'),
            availableDates: this.hasMany(AvailableDate, 'product_id'),
            priceExtensionNotFinished: this.hasMany(Element, 'product_id'),
            highlight: this.belongsTo(WebsiteHighlight, 'website_highlight_id'),
        }
    }

    /**
     *
     * @param params
     * @returns {*}
     */
    static search(params = {}) {
        return this.api()
            .filtered(params.filters)
            .with('activeVersion')
            .get();
    }

    /**
     *
     * @param tags
     * @returns {Promise<AxiosResponse<T>>}
     */
    syncInternalTags(tags) {
        return this.constructor.api().action(this.id + '/sync-internal-tags').update({'tags': tags}, false);
    }

    /**
     *
     * @param tags
     * @returns {Promise<AxiosResponse<T>>}
     */
    synchWebsiteTags(tags) {
        tags = map([].concat(...Object.keys(tags).map(val => tags[val])), o => o['id']);

        return this.constructor.api()
            .action(this.id + '/sync-website-tags')
            .update({'tags': tags}, false).then(() => {
                return this.constructor.api().with(['websiteProductTags.tag.type']).find(this.id);
            });
    }

    /**
     *
     * @param daysOfWeek
     * @returns {Promise<AxiosResponse<T>>}
     */
    syncDayOfWeeks(daysOfWeek) {
        return this.constructor.api()
            .action(this.id + '/sync-days-of-week')
            .update({'daysOfWeek': daysOfWeek}, false);
    }

    /**
     *
     * @param usps
     * @returns {Promise<AxiosResponse<T>>}
     */
    syncUsps(usps) {
        return this.constructor.api()
            .action(this.id + '/sync-usps')
            .update({'usps': usps}, false).then((product) => {
                ProductUniqueSellingPoint.query().deleteAll()

                if (product.productUsps) {
                    product.productUsps.forEach(usp => new ProductUniqueSellingPoint(usp));
                }
            });
    }

    static importTravalcoProduct(thirdPartyCode, productTypeCode) {
        return this.api()
            .action('travalco/import')
            .create({'third_party_product_code': thirdPartyCode, 'product_type_code': productTypeCode});
    }

    /**
     *
     * @param productId
     * @returns {*}
     */
    static loadProductDetails(productId) {
        return this.api()
            .with(['details', 'details.compositions', 'details.compositions.todaysCachedProductPrice'])
            .find(productId);
    }

    /**
     *
     * @param productId
     * @returns {*}
     */
    static loadDayToDays(productId) {
        return this.api()
            .with(['daytodays.images.publicImage'])
            .find(productId);
    }

    travalcoUrl() {
        let url;
        if (this.ActiveVersion().Type().code === 'H') {
            url =  '#/main/product/accommodation/' + this.travalco_code;
        } else {
            url =  '#/main/product/experience/' + this.travalco_code;
        }

        return window.config.TRAVALCO_URL + url;
    }

    /**
     *
     * @returns {*}
     */
    ProductDetails() {
        return this.getRelated('details');
    }

    /**
     *
     * @returns {*}
     */
    InternalTags() {
        return this.getRelated('productInternalTags');
    }

    /**
     *
     * @returns {*}
     */
    InternalTagsByType() {
        let tags = this.getRelated('productInternalTags');

        return mapKeys(
            groupBy(tags, 'product_internal_tag_type_id'),
            (value, key) => key);
    }

    /**
     *
     * @returns {*}
     */
    WebsiteProductTags() {
        return this.getRelated('websiteProductTags');
    }

    /**
     *
     * @returns {*}
     */
    ActiveProductDetails() {
        return this.getRelated('details', function (item) {
            return item.is_active && !item.is_created_on_the_fly;
        });
    }

    /**
     *
     * @returns {*}
     */
    AvailableDayOfWeeks() {
        return this.getRelated('availableDays');
    }

    /**
     *
     * @returns {*}
     */
    AvailableDates() {
        return this.getRelated('availableDates');
    }

    /**
     *
     * @returns {*}
     */
    Regions() {
        return this.getRelated('regions');
    }

    /**
     *
     * @returns {*}
     */
    RelatedProducts() {
        return this.getRelated('relatedProducts', function (item) {
            return item.relation_type_key === 'related';
        });
    }

    /**
     *
     * @returns {*}
     */
    Upgrades() {
        return this.getRelated('relatedProducts', function (item) {
            return item.relation_type_key === 'upgrade';
        });
    }

    /**
     *
     * @returns {*}
     */
    Downgrades() {
        return this.getRelated('relatedProducts', function (item) {
            return item.relation_type_key === 'downgrade';
        });
    }

    /**
     *
     * @returns {*}
     */
    RelatedHotels() {
        return this.getRelated('relatedProducts', function (item) {
            return item.relation_type_key === 'relatedHotel';
        });
    }

    /**
     *
     * @returns {boolean|*}
     */
    hasRelatedHotels() {
        return this.ActiveVersion() && this.ActiveVersion().Type().id === constants.productTypes.tour
    }

    /**
     *
     * @param ids
     * @returns {*}
     */
    static byIds(ids) {
        return this.all().filter(item => ids.includes(item.id));
    }

    /**
     *
     * @returns {*}
     */
    ProductNotices() {
        return this.getRelated('notices');
    }

    /**
     *
     * @returns {boolean|*}
     */
    ActiveVersion() {
        return this.getRelated('activeVersion');
    }

    /**
     *
     * @returns {*}
     */
    ProductUsps() {
        return this.getRelated('productUsps');
    }

    /**
     *
     * @returns {*}
     */
    WebsiteProduct() {
        return this.getRelated('websiteProduct');
    }

    /**
     *
     * @returns {Array}
     */
    ProductDaytodays() {
        return orderBy(this.getRelated('daytodays'), 'sort_order');
    }

    /**
     *
     * @returns {*}
     */
    WebsiteIncludeTransfers() {
        return this.getRelated('websiteIncludeTransfers');
    }

    /**
     *
     * @returns {*}
     */
    WebsiteRoute() {
        return WebsiteRoute.findByRouteable(this.id, 'product');
    }

    /**
     *
     * @returns {string}
     */
    FullUrl() {
        return this.ActiveVersion().FullUrl();
    }

    /**
     *
     * @param productId
     * @param productDetailId
     * @param compositionId
     * @param transferStartDate
     * @param transferEndDate
     * @param adults
     * @returns {Promise<AxiosResponse<T>>}
     */
    static getCompositionSalePrice(productId, productDetailId, compositionId, transferStartDate, transferEndDate, adults) {
        return this.api()
            .action('sale/' + productId + '/' + productDetailId + '/' + compositionId)
            .where('start_date', transferStartDate)
            .where('end_date', transferEndDate)
            .where('adults', adults)
            .disableErrorInPopup()
            .getSingle(false);
    }

    /**
     *
     * @param productId
     * @param adults
     * @param children
     * @param babies
     * @param startDate
     * @param endDate
     * @returns {*}
     */
    static getPossibleCombinations(productId, adults, children, babies, startDate, endDate) {
        return this.api()
            .action('sale/' + productId + '/combinations')
            .where('start_date', startDate)
            .where('end_date', endDate)
            .where('adults', adults)
            .where('children', children)
            .where('babies', babies)
            .getSingle(false);
    }

    static getUnassignedMarginProductCount() {
        return this.api()
            .action('travalco/without-margin-count')
            .getValue();
    }

    IsTravalco() {
        return Boolean(this.travalco_eid);
    }

    static getExternalAvailableDetails(productId, startDate, endDate) {
        return this.api().action('travalco/' + productId + '/available-details')
            .where('start_date', startDate)
            .where('end_date', endDate)
            .get(false);
    }
}
